import React, { useState } from "react";
import { graphql, Link } from "gatsby";
import {
  Box,
  Button,
  Typography,
  styled,
  Theme,
  Container,
  TextField,
  CircularProgress
} from "@material-ui/core";
import { formatMultilinesText } from "../../helpers/markdown";
import { InternationalPageSectionFragment as Props } from "./__generated__/InternationalPageSectionFragment";
import useTranslate from "../../hooks/use-translate";
import BasicModal from "../../components/basic-modal";
import { useForm } from "react-hook-form";
import { isEmail } from "validator";
import { subscribeToMailchimp } from "../../services/mailchimp";
import { Background as ImgixBackground } from "react-imgix";
import useIsMobile from "../../hooks/use-is-mobile";

type FormDataType = {
  email: string;
};

export const BackgroundWrapper = styled(ImgixBackground)(
  ({ theme }: { theme: Theme }) => ({
    position: "relative",
    overflow: "hidden",
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.primary.main,
    minHeight: `min(max(calc(100vh - var(--header-height)), 700px), 1200px)`, // Use min & max to not overstretch the hero
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: theme.spacing(6)
  })
);

export default function InternationalPageSection({
  title,
  subtitle,
  internationalPageBackground: background,
  internationalPageBackgroundMobile: backgroundMobile
}: Props) {
  const isMobile = useIsMobile();

  const t = useTranslate();
  const [modalIsOpen, setModalOpen] = useState(false);

  const { register, handleSubmit, errors, formState } = useForm<FormDataType>();
  const [submitted, setSubmitted] = useState(false);

  const handleFormSubmit = async (data: FormDataType) => {
    const tags = ["International Waitlist"];
    try {
      await subscribeToMailchimp({ email: data.email, tags });
    } catch (e) {}
    setSubmitted(true);
  };

  const backgroundUrl = isMobile ? backgroundMobile.url : background.url;

  return (
    <>
      <BackgroundWrapper src={backgroundUrl}>
        <Container maxWidth="md">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Box mb={2} maxWidth="600px">
              <Typography
                variant="h4"
                className="center"
                dangerouslySetInnerHTML={{
                  __html: formatMultilinesText(title)
                }}
              />
            </Box>
            {subtitle && (
              <Box mb={2}>
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{
                    __html: formatMultilinesText(subtitle)
                  }}
                  className="center"
                />
              </Box>
            )}

            <Box
              width="100%"
              maxWidth="420px"
              display="flex"
              flexDirection="column"
              alignItems="stretch"
            >
              <Box mb={1.5}>
                <Button
                  variant="contained"
                  fullWidth
                  className="express-delivery"
                  onClick={() => setModalOpen(true)}
                >
                  {t("international-page.open-modal")}
                </Button>
              </Box>
              <Button
                variant="contained"
                fullWidth
                component={Link}
                to={"/us/en/"}
              >
                {t("international-page.continue")}
              </Button>
            </Box>
          </Box>
        </Container>
      </BackgroundWrapper>

      <BasicModal
        open={modalIsOpen}
        onClose={() => setModalOpen(false)}
        BoxProps={{ minHeight: "350px" }}
      >
        <>
          {/* Modal content */}
          {!submitted && (
            <>
              {/* Form */}
              <Box mb={2}>
                <Typography variant="h5">
                  {t("international-page.modal-title")}
                </Typography>
              </Box>

              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                style={{ display: "block", width: "100%" }}
              >
                <Box mb={3} mt={1}>
                  <TextField
                    id="email"
                    name="email"
                    label={"Email address"}
                    fullWidth
                    inputRef={register({
                      required: true,
                      validate: (value: string) => {
                        if (!isEmail(value)) {
                          return t("error.message.email");
                        }
                        return true;
                      }
                    })}
                    error={!!errors.email}
                    disabled={formState.isSubmitting}
                    variant="outlined"
                    autoComplete="email"
                    helperText={errors.email?.message}
                  />
                </Box>

                <Box mb={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={formState.isSubmitting}
                  >
                    {!formState.isSubmitting && "Submit"}
                    {formState.isSubmitting && (
                      <CircularProgress color="secondary" size={16} />
                    )}
                  </Button>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    dangerouslySetInnerHTML={{
                      __html: t("age-verification.terms")
                    }}
                  />
                </Box>
              </form>
            </>
          )}

          {submitted && (
            <Typography variant="h5">
              {t("international-page.modal-confirmation")}
            </Typography>
          )}
        </>
      </BasicModal>
    </>
  );
}

export const InternationalPageSectionFragment = graphql`
  fragment InternationalPageSectionFragment on DatoCmsCModuleInternationalPageSection {
    title
    subtitle
    internationalPageBackground: background {
      url
    }
    internationalPageBackgroundMobile: backgroundMobile {
      url
    }
    model {
      apiKey
    }
  }
`;
